import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { V1_MCP_RISK_LEVELS, V1_MCP_TRANSPORT_NAMES, v1McpRiskLevelSchema, v1McpTransportSchema, v1PlatformSchema, } from './vocabulary';
/**
 * Public MCP-server inventory contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`. The internal shape
 * this adapts (`MCPServerListItem`) is deliberately *not* re-exported here — the
 * DTO layer in `routes/v1/dto/mcp-servers.ts` is the only place the two meet.
 */
/**
 * Whether the server runs locally or over the network.
 *
 * Derived internally from the transport; published as its own field because it
 * is the distinction a customer actually filters on, and because `transport`
 * carries more detail than most integrations want.
 */
export const v1McpServerTypeSchema = z.enum(['local', 'remote']).meta({
    id: 'V1McpServerType',
    title: 'MCP server type',
    description: "Whether the server runs on the endpoint user's machine (`local`) or is reached over the network (`remote`).",
});
/**
 * How the client reaches the server.
 *
 * The vocabulary and its labels live in `./vocabulary`, because the published
 * ids are hyphenated (`stdio-local`) while the internal column is snake_case and
 * the DTO needs both directions. An earlier revision of this file published the
 * internal values unchanged, on the stated grounds that "they appear on the
 * Inventory screen" — they do not: `MCPTransportBadge` renders
 * `transportLabels[transport]`, so the screen says "Command + Local Files".
 * `transportName` now carries that label.
 *
 * The three `stdio-*` variants stay distinct rather than collapsing to one
 * `stdio`: the distinction between a server launched from local files, from a
 * repository checkout, and from an installed package is exactly what a security
 * team wants to know.
 */
export { v1McpTransportSchema };
/**
 * Whether any instance of the server is currently running.
 *
 * The words the Inventory screen uses ("Active" / "Inactive"), lowercased. The
 * internal filter vocabulary for the same column is `running` / `idle`; the DTO
 * absorbs that rename so the public contract reads the way the product does.
 */
export const v1McpServerStatusSchema = z.enum(['active', 'inactive']).meta({
    id: 'V1McpServerStatus',
    title: 'MCP server status',
    description: 'Whether at least one instance of this server was running at the most recent report (`active`), or none were (`inactive`).',
});
/** One tool the server advertised at handshake, or was seen called with (KIR-4910). */
export const v1McpToolSchema = z
    .object({
    name: z.string().describe('The tool name, as the server advertised it.'),
    description: z
        .string()
        .nullable()
        .describe("The tool's own description, or null when the server did not supply one."),
})
    .meta({
    id: 'V1McpTool',
    title: 'MCP tool',
    description: 'A tool an MCP server advertised at handshake, or was seen called through an AI client.',
    examples: [
        {
            name: 'search_issues',
            description: 'Search issues and pull requests across the repository.',
        },
    ],
});
export { v1McpRiskLevelSchema };
/**
 * A server's AgentMesh risk (KIR-5108). AgentMesh owns the value; Kirin reads
 * it and stores the last known value, so `lastSyncedAt` says how fresh it is.
 */
export const v1McpServerRiskSchema = z
    .object({
    level: v1McpRiskLevelSchema,
    levelName: z
        .string()
        .describe('The risk level as the Kirin app labels it, e.g. "Partial scan" for `partial-scan`.'),
    lastSyncedAt: z
        .string()
        .datetime()
        .describe('When Kirin last read this value from AgentMesh, ISO 8601 UTC. If AgentMesh cannot be reached, Kirin keeps serving the last known value and this time stops advancing.'),
    subject: z
        .object({
        packageName: z.string().describe('The package AgentMesh assessed.'),
        version: z.string().describe('The installed version it assessed.'),
    })
        .nullable()
        .describe("Set when the server is remote and the level is the AgentMesh risk of a package that runs locally on your endpoints to reach it. The remote server itself is not assessed. Null when the level is the risk of the server's own package."),
})
    .meta({
    id: 'V1McpServerRisk',
    title: 'MCP server risk',
    description: "An MCP server's risk level as assessed by AgentMesh, and when Kirin last synced it. No numeric score is published.",
    examples: [
        {
            level: 'risky',
            levelName: 'Risky',
            lastSyncedAt: '2026-10-12T08:00:00Z',
            subject: null,
        },
    ],
});
/**
 * One MCP server in the organization's inventory.
 *
 * A row is a *server*, aggregated across every developer and machine that
 * reported it — which is why `versions` and `sources` are arrays and the counts
 * are counts. It is not one installation.
 */
export const v1McpServerSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this server within your organization. Stable, and safe to store and pass back to the by-ID and users endpoints. Do not parse it — its internal format is not part of this contract and may change.'),
    name: z
        .string()
        .describe("The server's configuration key, as it appears in the client config that declares it. This is the name shown on the Inventory screen."),
    serverName: z
        .string()
        .nullable()
        .describe('The name the server reported for itself at handshake, which can differ from the configuration key. Null when no handshake has been observed.'),
    versions: z
        .array(z.string().nullable())
        .describe("Every distinct version of this server's package found installed on your endpoints, newest first. Read from the installed artifact, not from the version the server reports about itself at handshake — several well-known servers hardcode that value and a hostile server can set it to anything. Empty when no endpoint has reported one yet, which is common: the version exists only on the machine, so a server can be inventoried long before any of them runs it. Read `resolutionMethods` to tell that apart from a server that can never have a version."),
    resolutionMethods: z
        .array(z.string())
        .describe("How the versions above were obtained, or why none were. Read from the installed artifact: `npx-cache` (npm's run-once cache), `pypi-running-process` (the environment a live Python server is running inside — the only one that proves the code is live), `config-venv-path` (the virtualenv the launch command names), `uv-tool-dir` (a `uv tool install` directory), `pipx-venv` (a pipx virtualenv), `uvx-cache-newest` (uv's ephemeral environment cache, newest first — the weakest of these, since a cached environment does not prove a client ever ran the server), `repo-digest` (a container image's registry digest, which for OCI is both the version and the integrity), `repo-digest-unverified-store` (the same digest from a daemon using the containerd image store, where that field is synthesised for locally built images too, so it names the bytes but does not prove a registry served them), `repo-digest-foreign-runtime` (the same read through podman, nerdctl or finch rather than Docker), `repo-digest-untrusted-endpoint` (the same digest, but read from a daemon the server's own configuration named — through -H/--context in its command line, DOCKER_HOST/DOCKER_CONTEXT in its env block, or a DOCKER_CONFIG relocating the context store those resolve against — the bytes are named correctly, but a configuration that picks both the image and the daemon asked about it is not independent evidence), `toolkit-profile-db` and `toolkit-catalog` (an image pinned by digest in the Docker MCP Toolkit's own state on that machine), `npm-global` and `npm-project` (a globally or project-installed npm package — the program the configured command runs, or the copy npx runs instead of downloading), `path-venv` (the Python environment the configured command lives in), `running-process` (the program a live server process is running), `pnpm-dlx-cache` (pnpm's run-once cache, newest first) and `bunx-cache` (the copy Bun downloaded for this server), `homebrew-cellar` (a Homebrew install), `app-bundle` (the version of the macOS app the command ships inside), `mcpb-manifest` (the bundle manifest of a Claude Desktop extension the server was installed from), `ide-extension` (the code-editor extension the server ships inside, read from that extension's own manifest) and `install-dir-version` (a version-named install folder — the weakest of these, since a folder name is a convention rather than a record). Cannot be read: `not-applicable` (runs remotely, can never have one), `not-installed` (configured but never spawned on that machine, so a version appears after its first run; also reported when the configuration pins a version that is not installed), `local-project` and `git-ref` (no published package coordinate to resolve), `no-resolver` (ecosystem recognised, resolver not shipped yet), `unsupported-runner` and `unsupported-subcommand` (launch command Kirin does not resolve), `spec-unreadable` (package spec could not be parsed), `npx-cache-miss` and `dlx-cache-miss` (nothing matching found in npm's, or pnpm's or Bun's, run-once cache), `npx-cache-unsettled` (cache was mid-write, so no answer would be trustworthy), `foreign-index` (configured to install from a private index, so the name may not be the public one), `ambiguous-environments` (several copies are running at different versions and Kirin will not choose one), `scan-cancelled` (the scan ended first; the next one retries), `not-pulled` (a container image that is configured but not in that machine's local store — not the same as unused, since a client that times out and aborts a large pull leaves the same trace), `daemon-unreachable` (the container runtime was not running or its socket could not be reached; the next scan retries), `transport-unsupported` (the runtime's address was found but this agent cannot speak that transport — the Windows named pipe, or a TLS endpoint needing a client certificate — which no retry changes), `daemon-endpoint-refused` (the server's own configuration pointed the agent at a container runtime on another host and the agent declined to contact it), `locally-built-no-repodigest` (an image built or loaded locally, which no registry has vouched for, so it has no published coordinate; its labels are claims, not evidence), `toolkit-gateway` (the Docker MCP Toolkit gateway entry itself, which stands for several servers reported as their own rows), `toolkit-entry-not-pinned` (the Toolkit lists that server by tag, so the gateway picks the image at spawn), `toolkit-profile-not-found`, `toolkit-not-configured` and `not-in-any-cached-catalog` (the Toolkit state needed to name the image was absent or did not list it). Empty when no endpoint has reported identity yet. Deliberately an open vocabulary rather than an enum: the agent ships independently and is often newer than this API, so treat an unrecognised value as \"not resolved\" instead of switching exhaustively."),
    publisher: z
        .string()
        .nullable()
        .describe('The publisher, when one could be determined.'),
    type: v1McpServerTypeSchema,
    transport: v1McpTransportSchema,
    transportName: z
        .string()
        .describe('The transport as the Kirin app labels it, e.g. "Command + Local Files" for `stdio-local`. Published alongside the identifier so a report reads the way the Inventory screen does.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The IDEs and coding agents this server was configured in, each with the display name the Kirin app shows.'),
    status: v1McpServerStatusSchema,
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this server configured.'),
    runningCount: z
        .number()
        .int()
        .describe('How many instances were running at the most recent report. Zero when `status` is `inactive`.'),
    url: z
        .string()
        .nullable()
        .describe('The endpoint URL, for network transports. Null for local commands and when the URL is unknown.'),
    tools: z
        .array(v1McpToolSchema)
        .nullable()
        .describe('The tools the server is known to expose: those advertised at the most recent handshake, plus tools seen called through an AI client when the endpoint could not list them itself (for example behind an authenticating gateway). Tools seen only through calls may lack a description and may be a subset of what the server offers; a successful handshake replaces them. Null — rather than empty — when no tools have been observed, so "exposes no tools" stays distinguishable from "we have not looked".'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this server was first reported, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report for any instance of this server, ISO 8601 UTC.'),
    risk: v1McpServerRiskSchema
        .nullable()
        .describe("The server's AgentMesh risk: the most severe level among the package versions installed on your endpoints. For a remote server reached through a package that runs on your endpoints, it is that package's risk, named in `subject`. Null when AgentMesh holds no record for any of them — including local servers and remote servers with no such package — and while AgentMesh risk is not enabled for your organization."),
})
    .meta({
    id: 'V1McpServer',
    title: 'MCP server',
    description: "One MCP server in your organization's inventory, aggregated across every endpoint user and machine that reported it.",
    examples: [
        {
            id: 'github',
            name: 'github',
            serverName: 'github-mcp-server',
            versions: ['0.9.2'],
            resolutionMethods: ['npx-cache'],
            publisher: 'github',
            type: 'local',
            transport: 'stdio-package',
            transportName: 'Command + Package',
            platforms: [
                { id: 'claude', name: 'Claude Code' },
                { id: 'cursor', name: 'Cursor' },
            ],
            status: 'active',
            userCount: 23,
            runningCount: 11,
            url: null,
            tools: [
                {
                    name: 'search_issues',
                    description: 'Search issues and pull requests across the repository.',
                },
            ],
            firstSeenAt: '2026-06-02T08:41:19Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
            risk: {
                level: 'risky',
                levelName: 'Risky',
                lastSyncedAt: '2026-10-12T08:00:00Z',
                subject: null,
            },
        },
    ],
});
/**
 * Sortable fields, named for the public field they sort.
 *
 * A closed set rather than "any field": a sort key is part of the frozen
 * contract, and the internal engine accepts keys this surface has no field for.
 */
export const v1McpServerSortFieldSchema = z
    .enum([
    'name',
    'publisher',
    'type',
    'transport',
    'status',
    'userCount',
    'firstSeenAt',
    'lastSeenAt',
    'risk',
])
    .describe('Which field to sort by. `risk` sorts in AgentMesh order — dangerous, risky, clean, partial-scan, unscanned — with servers that have no risk last in both directions.');
/**
 * The list query.
 *
 * Every filter is optional and ANDed with the rest; a comma-separated filter ORs
 * within itself. The set matches the filters the Inventory → MCP Servers screen
 * offers, per the ticket.
 *
 * **Deliberately unnamed** — no `.meta({ id })`. A schema with an `id` is
 * emitted as a `$ref` component, and `@fastify/swagger` cannot expand a `$ref`
 * into an OpenAPI parameter list: naming this one makes `GET /api/v1/openapi.json`
 * fail outright rather than degrade. Response and nested object schemas take ids;
 * querystring schemas do not.
 */
export const v1McpServerListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by configuration name.'),
    version: csvParam('Filter by version. Matches either the installed version in `versions` or the version the server reports about itself at handshake, so a filter written against a self-reported value keeps working now that `versions` publishes the installed one.'),
    publisher: csvParam('Filter by publisher.'),
    type: csvParam('Filter by server type (`local`, `remote`).'),
    transport: csvParam(`Filter by transport. One of: ${Object.keys(V1_MCP_TRANSPORT_NAMES).join(', ')}.`),
    status: csvParam('Filter by status (`active`, `inactive`).'),
    risk: csvParam(`Filter by AgentMesh risk level. One or more of: ${Object.values(V1_MCP_RISK_LEVELS).join(', ')}. Matches no server while AgentMesh risk is not enabled for your organization.`),
    // No `platform` filter, deliberately. The organization MCP table engine has no
    // sources/IDE filter — `buildMcpWhereFilters` handles name, version,
    // publisher, type, transport, tools and users, and logs a warning for anything
    // else. Publishing one would mean a caller narrowing by platform, receiving
    // the whole unfiltered list, and having no way to tell. `platforms` is on
    // every row, so the narrowing is done client-side until the engine supports it.
    sortBy: v1McpServerSortFieldSchema.default('userCount'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1McpServerListResponseSchema = z
    .object({
    data: z.array(v1McpServerSchema).describe('This page of MCP servers.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1McpServerList',
    title: 'MCP server list',
    description: "A page of the MCP servers configured across your organization's endpoint users.",
    examples: [
        {
            data: [
                {
                    id: 'github',
                    name: 'github',
                    serverName: 'github-mcp-server',
                    versions: ['0.9.2'],
                    resolutionMethods: ['npx-cache'],
                    publisher: 'github',
                    type: 'local',
                    transport: 'stdio-package',
                    transportName: 'Command + Package',
                    platforms: [
                        { id: 'claude', name: 'Claude Code' },
                        { id: 'cursor', name: 'Cursor' },
                    ],
                    status: 'active',
                    userCount: 23,
                    runningCount: 11,
                    url: null,
                    tools: [
                        {
                            name: 'search_issues',
                            description: 'Search issues and pull requests across the repository.',
                        },
                    ],
                    firstSeenAt: '2026-06-02T08:41:19Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                    risk: {
                        level: 'risky',
                        levelName: 'Risky',
                        lastSyncedAt: '2026-10-12T08:00:00Z',
                        subject: null,
                    },
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 61,
                totalPages: 3,
                hasMore: true,
            },
        },
    ],
});
/**
 * One developer who has a given MCP server configured.
 *
 * `id` is the same identifier the Inventory users resource publishes, so a
 * caller can join the two.
 */
/**
 * The one version one endpoint user is on (KIR-5042).
 *
 * An object rather than a bare string because a version without its provenance
 * is misleading: `installed` was read off the artifact on that user's machine,
 * `self-declared` is what the server says about itself at handshake — a value
 * several well-known servers hardcode and a hostile server can set to anything.
 * Publishing them as one field would let a caller treat a claim as a
 * measurement, which is the distinction the whole identity slice exists for.
 */
export const v1McpServerUserVersionSchema = z
    .object({
    value: z.string().describe('The version string, as reported.'),
    source: z
        .enum(['installed', 'self-declared'])
        .describe("`installed` was read from the package artifact on this user's machine and is evidence. `self-declared` is the value the server reported about itself at handshake and is a claim — several well-known servers hardcode it and never update it, and a hostile server can set it to anything. Only reported when no endpoint of this user has an installed version to read."),
    method: z
        .string()
        .nullable()
        .describe("How an installed version was obtained — the same open vocabulary as the server resource's `resolutionMethods`. Null for a self-declared version, which is an assertion rather than a measurement, so there is no method."),
})
    .meta({
    id: 'V1McpServerUserVersion',
    title: 'MCP server user version',
    description: 'The version one endpoint user is on, and how it was obtained.',
    examples: [{ value: '0.9.2', source: 'installed', method: 'npx-cache' }],
});
export const v1McpServerUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The IDEs and coding agents in which this endpoint user has the server configured.'),
    serverNames: z
        .array(z.string())
        .describe('The handshake-reported server names seen for this endpoint user, which can differ from the configuration key.'),
    running: z
        .boolean()
        .describe('Whether the server was running for this endpoint user at their most recent report.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe("This endpoint user's most recent report, ISO 8601 UTC."),
    version: v1McpServerUserVersionSchema
        .nullable()
        .describe("The one version this endpoint user is on: the highest version installed on any of their machines, or — only when none of them has one — their highest handshake claim. Null is a real and common answer: a remote server has no installed copy to read, and a configured-but-never-spawned one has nothing downloaded yet. The server resource's `resolutionMethods` explains which. Users can legitimately disagree, which is why this is per-user rather than one number for the server."),
})
    .meta({
    id: 'V1McpServerUser',
    title: 'MCP server user',
    description: 'An endpoint user who has a given MCP server configured.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            platforms: [{ id: 'claude', name: 'Claude Code' }],
            serverNames: ['github-mcp-server'],
            running: true,
            lastSeenAt: '2026-08-31T14:03:07Z',
            version: { value: '0.9.2', source: 'installed', method: 'npx-cache' },
        },
    ],
});
/**
 * The users of one MCP server.
 *
 * Not paginated: the response is bounded by the developers in one organization
 * who have one server configured, and the internal handler returns the set in
 * one query. A `pagination` object would promise a walk this resource does not
 * implement.
 */
export const v1McpServerUserListResponseSchema = z
    .object({
    data: z
        .array(v1McpServerUserSchema)
        .describe('Every endpoint user who has this server configured.'),
})
    .meta({
    id: 'V1McpServerUserList',
    title: 'MCP server users',
    description: 'Every endpoint user in your organization who has a given MCP server configured. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    platforms: [{ id: 'claude', name: 'Claude Code' }],
                    serverNames: ['github-mcp-server'],
                    running: true,
                    lastSeenAt: '2026-08-31T14:03:07Z',
                    version: {
                        value: '0.9.2',
                        source: 'installed',
                        method: 'npx-cache',
                    },
                },
            ],
        },
    ],
});
// ---------------------------------------------------------------------------
// Risk detail sub-resource (KIR-5108) — UNPUBLISHED (hidden from OpenAPI until
// AgentMesh MCP risk is released; see NAMING.md). Not baselined in the
// contract snapshot: a baseline would freeze a shape nobody has been shown.
// ---------------------------------------------------------------------------
/** Why a server has no AgentMesh risk value. */
export const v1McpServerRiskUnavailableReasonSchema = z
    .enum(['remote', 'local', 'gateway', 'no-record'])
    .meta({
    id: 'V1McpServerRiskUnavailableReason',
    title: 'MCP server risk unavailable reason',
    description: 'Why a server has no AgentMesh risk. `remote`: a network server with no installed package. `local`: launched from local files or a repository checkout, with no published package. `gateway`: managed by a gateway, with no per-endpoint package. `no-record`: no AgentMesh result for the installed package versions yet — AgentMesh holds no record, or the versions have not been synced.',
});
/** One installed package version under a server, with its own verdict. */
export const v1McpServerRiskVersionSchema = z
    .object({
    ecosystem: z
        .string()
        .describe('The package ecosystem: `npm`, `pypi` or `oci`.'),
    packageName: z.string().describe('The package name, as installed.'),
    version: z.string().describe('The installed version.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users run this version.'),
    risk: v1McpServerRiskSchema
        .nullable()
        .describe("This version's AgentMesh risk, or null when AgentMesh holds no record."),
    riskFactors: z
        .array(z.string())
        .describe('The top risk factors AgentMesh found, most severe first.'),
    reportUrl: z
        .string()
        .nullable()
        .describe('The full report for this version in AgentMesh.'),
})
    .meta({
    id: 'V1McpServerRiskVersion',
    title: 'MCP server risk version',
    description: 'One package version installed under an MCP server, with its AgentMesh risk.',
});
export const v1McpServerRiskDetailSchema = z
    .object({
    id: z
        .string()
        .describe("The server's `id`, as returned by the list endpoint. Opaque."),
    risk: v1McpServerRiskSchema
        .nullable()
        .describe("The server's risk: the most severe level among its installed versions."),
    unavailableReason: v1McpServerRiskUnavailableReasonSchema
        .nullable()
        .describe('Why `risk` is null; null when it is set.'),
    riskFactors: z
        .array(z.string())
        .describe('The top risk factors of the most severe version, most severe first. No numeric score is published.'),
    reportUrl: z
        .string()
        .nullable()
        .describe('The full report for the most severe version in AgentMesh.'),
    versions: z
        .array(v1McpServerRiskVersionSchema)
        .describe('Every installed package version, most severe first.'),
})
    .meta({
    id: 'V1McpServerRiskDetail',
    title: 'MCP server risk detail',
    description: "An MCP server's AgentMesh risk with its top risk factors, report link and per-version breakdown.",
});
//# sourceMappingURL=mcp-servers.js.map